(function () {
  'use strict';

  class FashionGallerySlider1 {
    constructor(slider) {
      this.slider = slider;
      this.track = slider.querySelector('.fashion-gallery__track-1');
      this.items = Array.from(this.track?.querySelectorAll('.fashion-gallery__item-1') || []);

      const container = slider.parentElement || slider;
      this.prevBtn = container.querySelector('.fashion-gallery__nav--prev-1') || null;
      this.nextBtn = container.querySelector('.fashion-gallery__nav--next-1') || null;

      this.desktopColumns = parseInt(slider.dataset.desktopColumns, 10) || 4;
      this.mobileColumns = parseInt(slider.dataset.mobileColumns, 10) || 2;

      this.currentIndex = 0;
      this.isDragging = false;
      this.startPos = 0;
      this.currentTranslate = 0;
      this.prevTranslate = 0;
      this.animationID = 0;
      this.dragMoved = false;
      this.pointerId = null;
      this.startTarget = null; // 👈 lưu lại phần tử ban đầu được chạm

      this.onResize = this.onResize.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onDragStart = (e) => e.preventDefault();

      this.onPrevClick = (e) => { e.preventDefault(); this.prev(); };
      this.onNextClick = (e) => { e.preventDefault(); this.next(); };

      this.ro = null;

      this.init();
    }

    init() {
      if (!this.track || !this.items.length) return;

      this.slider.style.cursor = 'grab';
      this.slider.style.touchAction = 'none'; // 👈 cho phép cả vuốt ngang và dọc
      this.track.style.willChange = 'transform';

      this.addAccessibility();
      this.setPositionByIndex(false);
      this.updateButtons();
      this.addEventListeners();

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.refresh());
        this.ro.observe(this.track);
      }
    }

    destroy() {
      cancelAnimationFrame(this.animationID);

      window.removeEventListener('resize', this.onResize);
      this.slider.removeEventListener('pointerdown', this.onPointerDown);
      this.slider.removeEventListener('pointermove', this.onPointerMove);
      this.slider.removeEventListener('pointerup', this.onPointerUp);
      this.slider.removeEventListener('pointercancel', this.onPointerUp);
      this.slider.removeEventListener('pointerleave', this.onPointerUp);
      this.slider.removeEventListener('lostpointercapture', this.onPointerUp);

      this.slider.removeEventListener('keydown', this.onKeyDown);
      this.slider.removeEventListener('click', this.onClickCapture, true);
      this.slider.removeEventListener('dragstart', this.onDragStart);

      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrevClick);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNextClick);

      if (this.ro) {
        this.ro.disconnect();
        this.ro = null;
      }
    }

    addEventListeners() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrevClick);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNextClick);

      this.slider.addEventListener('pointerdown', this.onPointerDown);
      this.slider.addEventListener('pointermove', this.onPointerMove);
      this.slider.addEventListener('pointerup', this.onPointerUp);
      this.slider.addEventListener('pointercancel', this.onPointerUp);
      this.slider.addEventListener('pointerleave', this.onPointerUp);
      this.slider.addEventListener('lostpointercapture', this.onPointerUp);

      this.slider.addEventListener('click', this.onClickCapture, true);
      this.slider.addEventListener('dragstart', this.onDragStart);
      this.slider.addEventListener('keydown', this.onKeyDown);

      window.addEventListener('resize', this.onResize, { passive: true });
    }

    addAccessibility() {
      this.slider.setAttribute('role', 'region');
      this.slider.setAttribute('aria-roledescription', 'carousel');
      const label = this.slider.getAttribute('aria-label') || this.slider.dataset.label || 'Carousel';
      this.slider.setAttribute('aria-label', label);
      if (!this.slider.hasAttribute('tabindex')) this.slider.tabIndex = 0;

      if (this.prevBtn) this.prevBtn.setAttribute('aria-label', 'Previous slide');
      if (this.nextBtn) this.nextBtn.setAttribute('aria-label', 'Next slide');
    }

    getItemsPerView() {
      return window.innerWidth <= 768 ? this.mobileColumns : this.desktopColumns;
    }

    getMaxIndex() {
      const itemsPerView = this.getItemsPerView();
      return Math.max(0, this.items.length - itemsPerView);
    }

    getGap() {
      const style = getComputedStyle(this.track);
      const gap = parseInt(style.gap || style.columnGap || 0, 10);
      return isNaN(gap) ? 0 : gap;
    }

    getStep() {
      const first = this.items[0];
      const itemWidth = first ? first.getBoundingClientRect().width : (this.track.clientWidth / this.getItemsPerView());
      return (itemWidth || 0) + this.getGap();
    }

    clampTranslate(x) {
      const step = this.getStep();
      const min = -this.getMaxIndex() * step;
      const max = 0;
      return Math.max(min, Math.min(max, x));
    }

    setButtonState(btn, disabled) {
      if (!btn) return;
      if ('disabled' in btn) btn.disabled = disabled;
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      if (disabled) btn.setAttribute('tabindex', '-1');
      else btn.removeAttribute('tabindex');
    }

    setPositionByIndex(animate = true) {
      if (!this.items.length) return;
      const step = this.getStep();
      this.currentTranslate = -this.currentIndex * step;
      this.prevTranslate = this.currentTranslate;

      this.track.style.transition = animate ? 'transform 300ms ease' : 'none';
      this.track.style.transform = `translate3d(${this.currentTranslate}px, 0, 0)`;

      this.updateButtons();
    }

    updateButtons() {
      const atStart = this.currentIndex === 0;
      const atEnd = this.currentIndex >= this.getMaxIndex();
      this.setButtonState(this.prevBtn, atStart);
      this.setButtonState(this.nextBtn, atEnd);
    }

    next() {
      if (this.currentIndex < this.getMaxIndex()) {
        this.currentIndex++;
        this.setPositionByIndex(true);
      }
    }

    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.setPositionByIndex(true);
      }
    }

    refresh() {
      this.items = Array.from(this.track?.querySelectorAll('.fashion-gallery__item-1') || []);
      if (this.currentIndex > this.getMaxIndex()) {
        this.currentIndex = this.getMaxIndex();
      }
      this.setPositionByIndex(false);
    }

    onResize() {
      if (this.currentIndex > this.getMaxIndex()) {
        this.currentIndex = this.getMaxIndex();
      }
      this.setPositionByIndex(false);
    }

    // 👇 cho phép click link và vẫn drag được
    onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.isDragging = true;
      this.dragMoved = false;
      this.startPos = e.clientX;
      this.pointerId = e.pointerId;
      this.startTarget = e.target.closest('a') || null; // lưu link (nếu click vào ảnh)

      this.track.style.transition = 'none';

      if (this.slider.setPointerCapture && this.pointerId != null) {
        try { this.slider.setPointerCapture(this.pointerId); } catch (_) {}
      }

      this.animationID = requestAnimationFrame(() => this.animation());
      this.slider.classList.add('is-dragging');
      this.slider.style.cursor = 'grabbing';
    }

    onPointerMove(e) {
      if (!this.isDragging) return;

      const dx = e.clientX - this.startPos;
      const nextTranslate = this.prevTranslate + dx;
      const clamped = this.clampTranslate(nextTranslate);
      this.currentTranslate = clamped;

      if (Math.abs(dx) > 5) {
        this.dragMoved = true;
      }
    }

    onPointerUp() {
      if (!this.isDragging) return;

      this.isDragging = false;
      cancelAnimationFrame(this.animationID);

      if (this.slider.releasePointerCapture && this.pointerId != null) {
        try { this.slider.releasePointerCapture(this.pointerId); } catch (_) {}
      }
      this.pointerId = null;

      const movedBy = this.currentTranslate - this.prevTranslate;
      const step = this.getStep();
      const threshold = Math.min(step * 0.25, 100);

      if (movedBy < -threshold && this.currentIndex < this.getMaxIndex()) {
        this.currentIndex++;
      } else if (movedBy > threshold && this.currentIndex > 0) {
        this.currentIndex--;
      }

      this.setPositionByIndex(true);
      this.slider.classList.remove('is-dragging');
      this.slider.style.cursor = 'grab';

      // 👇 nếu không kéo, trigger click cho link
      if (!this.dragMoved && this.startTarget) {
        this.startTarget.click();
      }
    }

    // 👇 chỉ chặn click khi thật sự kéo
    onClickCapture(e) {
      if (this.dragMoved && Math.abs(this.currentTranslate - this.prevTranslate) > 5) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.dragMoved = false;
      this.startTarget = null;
    }

    onKeyDown(e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      }
    }

    animation() {
      this.track.style.transform = `translate3d(${this.currentTranslate}px, 0, 0)`;
      if (this.isDragging) {
        this.animationID = requestAnimationFrame(() => this.animation());
      }
    }
  }

  function initFashionGallerySliders(root = document) {
    root.querySelectorAll('.fashion-gallery__slider-1').forEach((slider) => {
      if (slider.__fashionGalleryInstance) {
        slider.__fashionGalleryInstance.refresh();
      } else {
        slider.__fashionGalleryInstance = new FashionGallerySlider1(slider);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => initFashionGallerySliders());
  window.addEventListener('load', () => initFashionGallerySliders());

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      initFashionGallerySliders(e.target || document);
    });

    document.addEventListener('shopify:section:unload', (e) => {
      (e.target || document).querySelectorAll('.fashion-gallery__slider-1').forEach((slider) => {
        if (slider.__fashionGalleryInstance) {
          slider.__fashionGalleryInstance.destroy();
          slider.__fashionGalleryInstance = null;
        }
      });
    });
  }
})();
